import axiosHttp from "./interceptor";
import { useState, useEffect, createContext, useMemo, useCallback } from "react";
import { ClaimURLs } from "../constants/claims";
import { FeatureToggleURLs, FeatureType } from "../constants/FeatureToggles";
import Loader from "../common/Loader/Loader";
import PropTypes from "prop-types";

export const ClaimsContext = createContext([]);

export const ClaimsProvider = ({ children }) => {
	const [userClaims, setUserClaims] = useState(null);
	const [userFeatures, setUserFeatures] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [userClaimsAreLoaded, setUserClaimsAreLoaded] = useState(false);
	const [userFeaturesAreLoaded, setUserFeaturesAreLoaded] = useState(false);
	const [currentWcn, setCurrentWcn] = useState(null);
	const [isAuthenticated, setIsAuthenticated] = useState(false);

	// function to reset the claims and authentication state
	const resetClaims = () => {
		setUserClaims(null);
		setIsLoading(false);
		setUserClaimsAreLoaded(false);
		setIsAuthenticated(false);
	};

	const fetchUserClaims = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await axiosHttp.post(ClaimURLs.GetUserClaims, null, {
				signal: AbortSignal.timeout(5000), // 5 seconds timeout
			});
			setUserClaims(response.data);

			if (response.data && response.data.length > 0) {
				setUserClaimsAreLoaded(true);
			} else {
				setUserClaimsAreLoaded(false);
			}
		} catch (error) {
			console.error("Failed to fetch user claims:", error);
		} finally {
			setIsLoading(false);
		}
	}, []);

	const fetchUserFeatures = useCallback(async (wcn) => {
		const url = FeatureToggleURLs.CheckFeatures;
		const featuresToCheck = {
			FeatureNames: Object.values(FeatureType),
		};

		if (wcn !== undefined && wcn !== null && `${wcn}`.trim() !== "") {
			featuresToCheck.Wcn = Number(wcn);
			setCurrentWcn(wcn);
		}

		const response = await axiosHttp.post(url, featuresToCheck, {
			signal: AbortSignal.timeout(5000), // 5 seconds timeout
		});

		setUserFeatures(response.data);
		setUserFeaturesAreLoaded(true);
	}, []);

	useEffect(() => {
		if (isAuthenticated) {
			if (!userClaimsAreLoaded) {
				fetchUserClaims();
			}
			if (!userFeaturesAreLoaded) {
				fetchUserFeatures();
			}
		}
	}, [isAuthenticated, userClaimsAreLoaded, userFeaturesAreLoaded, fetchUserClaims, fetchUserFeatures]);

	const userHasClaim = useCallback(
		(claim) => (userClaims ? userClaims.includes(claim) : false),
		[userClaims]
	);

	const userHasFeature = useCallback(
		(featureName) => {
			if (userFeatures && Array.isArray(userFeatures)) {
				return userFeatures.some(
					(x) => x.featureName === featureName && x.isEnabled === true
				);
			}
			return false;
		},
		[userFeatures]
	);

	const checkWcnDelta = useCallback((wcn) => wcn !== currentWcn, [currentWcn]);

	const contextValue = useMemo(
		() => ({
			userClaims,
			userHasClaim,
			isLoading,
			userClaimsAreLoaded,
			userFeaturesAreLoaded,
			isAuthenticated,
			userHasFeature,
			// functions
			setIsAuthenticated,
			resetClaims,
			fetchUserFeatures,
			checkWcnDelta
		}),
		[
			userClaims,
			userHasClaim,
			userHasFeature,
			isLoading,
			userClaimsAreLoaded,
			userFeaturesAreLoaded,
			isAuthenticated,
			fetchUserFeatures,
			checkWcnDelta
		]
	);

	return (
		<ClaimsContext.Provider value={contextValue}>
			<RenderChildren isLoading={isLoading}>{children}</RenderChildren>
		</ClaimsContext.Provider>
	);
};

ClaimsProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

/**
 * This function is used to render the children of the component
 * It checks if the isLoading prop is true or false
 * If true, it renders the Loader component
 * If false, it renders the children of the component
 * @returns {JSX.Element} - The rendered children or the Loader component
 */
const RenderChildren = ({ isLoading, children }) => {
	if (isLoading) {
		return <Loader loaderName="loading..." />;
	}
	return children;
};

RenderChildren.propTypes = {
	isLoading: PropTypes.bool.isRequired,
	children: PropTypes.node,
};
